import { useCallback, useEffect, useState } from "react";
import { getRetention, setRetention, type QaRetention } from "../../api/qaOrchestrator";
import { useAction, type SetError } from "./qaShared";

const FIELDS: { key: keyof QaRetention; kind: string; outcome: string }[] = [
  { key: "videoPassDays", kind: "Videos", outcome: "passed" },
  { key: "videoFailDays", kind: "Videos", outcome: "failed" },
  { key: "screenshotPassDays", kind: "Screenshots and logs", outcome: "passed" },
  { key: "screenshotFailDays", kind: "Screenshots and logs", outcome: "failed" },
];

const valid = (v: string) => /^\d+$/.test(v) && Number(v) <= 3650;
const days = (n: number) => (n === 0 ? "deleted at the next cleanup" : `kept ${n} day${n === 1 ? "" : "s"}`);

/** How long the orchestrator keeps a finished run's files (GET/PUT /settings/retention). */
export default function QaRetentionSection({ setError }: { setError: SetError }) {
  const [saved, setSaved] = useState<QaRetention | null>(null);
  // The orchestrator's own defaults (GET /settings/retention), not a copy kept here.
  const [defaults, setDefaults] = useState<QaRetention | null>(null);
  const [form, setForm] = useState<Record<keyof QaRetention, string> | null>(null);
  const load = useCallback(async () => {
    const { retention: r, defaults: d } = await getRetention();
    setSaved(r);
    setDefaults(d ?? null);
    setForm(Object.fromEntries(FIELDS.map((f) => [f.key, String(r[f.key])])) as Record<keyof QaRetention, string>);
  }, []);
  const { busy, run } = useAction(load, setError);
  useEffect(() => { load().catch((e) => setError((e as Error).message)); }, [load, setError]);

  const allValid = !!form && FIELDS.every((f) => valid(form[f.key]));
  const changed = !!form && !!saved && FIELDS.some((f) => form[f.key] !== String(saved[f.key]));
  const save = (values: QaRetention) => run(() => setRetention(values));

  return (
    <section className="qo-section">
      <div className="qo-section-head">
        <div>
          <h3>How long run files are kept</h3>
          <p>
            The orchestrator keeps every finished run's videos, screenshots and logs, so its page and its pull request comment keep working. After the number
            of days below, counted from when the run finished, they are deleted; the run itself stays in the history. Logs are kept as long as the screenshots.
            0 deletes them at the next hourly cleanup.
          </p>
        </div>
      </div>
      {!form ? (
        <div className="members-empty">Loading…</div>
      ) : (
        <div className="qo-card qo-form">
          <div className="qo-grid qo-retention">
            {FIELDS.map((f) => (
              <label key={f.key}>
                {f.kind} of a {f.outcome} run
                <span className="qo-days">
                  <input aria-label={f.key} type="number" min={0} max={3650} value={form[f.key]} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} />
                  days
                </span>
                <small>{valid(form[f.key]) ? days(Number(form[f.key])) : "a whole number from 0 to 3650"}{defaults ? ` · default ${defaults[f.key]}` : ""}</small>
              </label>
            ))}
          </div>
          <div className="qo-actions">
            <button className="btn btn-accent" disabled={busy || !allValid || !changed} onClick={() => void save(Object.fromEntries(FIELDS.map((f) => [f.key, Number(form[f.key])])) as unknown as QaRetention)}>Save</button>
            <button className="btn btn-ghost" disabled={busy || !changed} onClick={() => void load()}>Undo changes</button>
            {defaults && <button className="btn btn-ghost" disabled={busy || FIELDS.every((f) => saved?.[f.key] === defaults[f.key])} onClick={() => void save(defaults)}>Reset to defaults</button>}
          </div>
          <p className="qo-hint">A shorter time applies to runs that already finished too: their files go at the next cleanup.</p>
        </div>
      )}
    </section>
  );
}
